// src/lib/taxonomy/agency-domains.ts
// Federal agency domain mappings for capability inference
// Focus: Major federal agencies and their domains

import { AgencyDomainNode } from './types';

export const AGENCY_DOMAINS: AgencyDomainNode[] = [
  // ============================================================
  // DEPARTMENT OF DEFENSE
  // ============================================================
  {
    id: 'dod',
    name: 'Department of Defense',
    aliases: ['DoD', 'DOD', 'Defense Department', 'Pentagon'],
    domain: 'defense',
    subDomains: ['military', 'national_security', 'logistics'],
    proves: [
      { capability: 'defense_contracting', confidence: 0.95 },
      { capability: 'military_operations_support', confidence: 0.90 },
      { capability: 'security_clearance_work', confidence: 0.90 },
      { capability: 'mission_critical_systems', confidence: 0.85 },
      { capability: 'dod_compliance', confidence: 0.90 }
    ],
    typicalNaics: ['541512', '541511', '541330', '541519', '541690'],
    typicalSetAsides: ['SDVOSB', '8(a)', 'SB']
  },
  {
    id: 'army',
    name: 'Department of the Army',
    aliases: ['Army', 'US Army', 'U.S. Army', 'United States Army'],
    department: 'Department of Defense',
    domain: 'defense',
    subDomains: ['military', 'ground_forces', 'logistics'],
    proves: [
      { capability: 'defense_contracting', confidence: 0.95 },
      { capability: 'army_operations_support', confidence: 0.95 },
      { capability: 'military_logistics', confidence: 0.90 },
      { capability: 'combat_systems', confidence: 0.85 }
    ],
    typicalNaics: ['541512', '541330', '541690', '561210'],
    typicalSetAsides: ['SDVOSB', '8(a)', 'SB']
  },
  {
    id: 'navy',
    name: 'Department of the Navy',
    aliases: ['Navy', 'US Navy', 'U.S. Navy', 'United States Navy', 'USN'],
    department: 'Department of Defense',
    domain: 'defense',
    subDomains: ['military', 'maritime', 'naval_operations'],
    proves: [
      { capability: 'defense_contracting', confidence: 0.95 },
      { capability: 'navy_operations_support', confidence: 0.95 },
      { capability: 'maritime_systems', confidence: 0.90 },
      { capability: 'shipboard_systems', confidence: 0.85 }
    ],
    typicalNaics: ['541512', '541330', '336611', '541690'],
    typicalSetAsides: ['SDVOSB', '8(a)', 'SB']
  },
  {
    id: 'air_force',
    name: 'Department of the Air Force',
    aliases: ['Air Force', 'US Air Force', 'U.S. Air Force', 'USAF', 'United States Air Force'],
    department: 'Department of Defense',
    domain: 'defense',
    subDomains: ['military', 'aerospace', 'air_operations'],
    proves: [
      { capability: 'defense_contracting', confidence: 0.95 },
      { capability: 'air_force_operations_support', confidence: 0.95 },
      { capability: 'aerospace_systems', confidence: 0.90 },
      { capability: 'space_systems', confidence: 0.80 }
    ],
    typicalNaics: ['541512', '541330', '336411', '541690'],
    typicalSetAsides: ['SDVOSB', '8(a)', 'SB']
  },
  {
    id: 'space_force',
    name: 'United States Space Force',
    aliases: ['Space Force', 'USSF', 'US Space Force'],
    department: 'Department of Defense',
    domain: 'defense',
    subDomains: ['military', 'space', 'satellite_operations'],
    proves: [
      { capability: 'defense_contracting', confidence: 0.95 },
      { capability: 'space_operations_support', confidence: 0.95 },
      { capability: 'satellite_systems', confidence: 0.90 },
      { capability: 'space_systems', confidence: 0.95 }
    ],
    typicalNaics: ['541512', '541330', '336414', '541690'],
    typicalSetAsides: ['SDVOSB', '8(a)', 'SB']
  },
  {
    id: 'disa',
    name: 'Defense Information Systems Agency',
    aliases: ['DISA'],
    department: 'Department of Defense',
    domain: 'defense',
    subDomains: ['it', 'communications', 'cybersecurity'],
    proves: [
      { capability: 'defense_it', confidence: 0.95 },
      { capability: 'military_communications', confidence: 0.95 },
      { capability: 'dod_network_infrastructure', confidence: 0.90 },
      { capability: 'cybersecurity', confidence: 0.90 },
      { capability: 'enterprise_it', confidence: 0.85 }
    ],
    typicalNaics: ['541512', '541511', '541519', '517110'],
    typicalSetAsides: ['SDVOSB', '8(a)', 'SB']
  },

  // ============================================================
  // DEPARTMENT OF VETERANS AFFAIRS
  // ============================================================
  {
    id: 'va',
    name: 'Department of Veterans Affairs',
    aliases: ['VA', 'Veterans Affairs', 'DVA'],
    domain: 'veterans',
    subDomains: ['healthcare', 'benefits', 'memorial'],
    proves: [
      { capability: 'healthcare_it', confidence: 0.90 },
      { capability: 'veteran_services', confidence: 0.95 },
      { capability: 'hipaa_compliance', confidence: 0.90 },
      { capability: 'benefits_administration', confidence: 0.85 },
      { capability: 'electronic_health_records', confidence: 0.85 }
    ],
    typicalNaics: ['541512', '541511', '621999', '524292'],
    typicalSetAsides: ['SDVOSB', 'VOSB']
  },
  {
    id: 'vha',
    name: 'Veterans Health Administration',
    aliases: ['VHA'],
    department: 'Department of Veterans Affairs',
    domain: 'healthcare',
    subDomains: ['medical', 'clinical', 'health_it'],
    proves: [
      { capability: 'healthcare_it', confidence: 0.95 },
      { capability: 'clinical_systems', confidence: 0.95 },
      { capability: 'hipaa_compliance', confidence: 0.95 },
      { capability: 'electronic_health_records', confidence: 0.95 },
      { capability: 'medical_device_integration', confidence: 0.85 }
    ],
    typicalNaics: ['541512', '541511', '621999', '621511'],
    typicalSetAsides: ['SDVOSB', 'VOSB']
  },

  // ============================================================
  // DEPARTMENT OF HEALTH AND HUMAN SERVICES
  // ============================================================
  {
    id: 'hhs',
    name: 'Department of Health and Human Services',
    aliases: ['HHS', 'Health and Human Services'],
    domain: 'healthcare',
    subDomains: ['public_health', 'human_services', 'medical_research'],
    proves: [
      { capability: 'healthcare_it', confidence: 0.90 },
      { capability: 'public_health_systems', confidence: 0.90 },
      { capability: 'hipaa_compliance', confidence: 0.95 },
      { capability: 'health_data_analytics', confidence: 0.85 }
    ],
    typicalNaics: ['541512', '541511', '541720', '621999'],
    typicalSetAsides: ['SB', '8(a)', 'WOSB']
  },
  {
    id: 'cms',
    name: 'Centers for Medicare and Medicaid Services',
    aliases: ['CMS'],
    department: 'Department of Health and Human Services',
    domain: 'healthcare',
    subDomains: ['medicare', 'medicaid', 'health_insurance'],
    proves: [
      { capability: 'healthcare_it', confidence: 0.95 },
      { capability: 'claims_processing', confidence: 0.95 },
      { capability: 'hipaa_compliance', confidence: 0.95 },
      { capability: 'large_scale_data_systems', confidence: 0.90 },
      { capability: 'healthcare_regulations', confidence: 0.90 }
    ],
    typicalNaics: ['541512', '541511', '524292', '541611'],
    typicalSetAsides: ['SB', '8(a)']
  },
  {
    id: 'cdc',
    name: 'Centers for Disease Control and Prevention',
    aliases: ['CDC'],
    department: 'Department of Health and Human Services',
    domain: 'healthcare',
    subDomains: ['public_health', 'epidemiology', 'disease_surveillance'],
    proves: [
      { capability: 'public_health_systems', confidence: 0.95 },
      { capability: 'epidemiological_data', confidence: 0.95 },
      { capability: 'health_data_analytics', confidence: 0.90 },
      { capability: 'laboratory_systems', confidence: 0.85 }
    ],
    typicalNaics: ['541512', '541511', '541720', '541380'],
    typicalSetAsides: ['SB', '8(a)']
  },
  {
    id: 'nih',
    name: 'National Institutes of Health',
    aliases: ['NIH'],
    department: 'Department of Health and Human Services',
    domain: 'science',
    subDomains: ['medical_research', 'biomedical', 'clinical_trials'],
    proves: [
      { capability: 'research_it', confidence: 0.95 },
      { capability: 'biomedical_informatics', confidence: 0.90 },
      { capability: 'scientific_data_management', confidence: 0.90 },
      { capability: 'clinical_trial_systems', confidence: 0.85 }
    ],
    typicalNaics: ['541512', '541511', '541720', '541714'],
    typicalSetAsides: ['SB', '8(a)']
  },

  // ============================================================
  // DEPARTMENT OF HOMELAND SECURITY
  // ============================================================
  {
    id: 'dhs',
    name: 'Department of Homeland Security',
    aliases: ['DHS', 'Homeland Security'],
    domain: 'homeland_security',
    subDomains: ['border_security', 'cybersecurity', 'emergency_management'],
    proves: [
      { capability: 'homeland_security_work', confidence: 0.95 },
      { capability: 'security_systems', confidence: 0.90 },
      { capability: 'cybersecurity', confidence: 0.90 },
      { capability: 'critical_infrastructure_protection', confidence: 0.85 }
    ],
    typicalNaics: ['541512', '541511', '541519', '561621'],
    typicalSetAsides: ['SB', '8(a)', 'SDVOSB']
  },
  {
    id: 'cbp',
    name: 'Customs and Border Protection',
    aliases: ['CBP', 'Border Patrol', 'US Customs'],
    department: 'Department of Homeland Security',
    domain: 'homeland_security',
    subDomains: ['border_security', 'customs', 'trade'],
    proves: [
      { capability: 'homeland_security_work', confidence: 0.95 },
      { capability: 'border_security_systems', confidence: 0.95 },
      { capability: 'biometric_systems', confidence: 0.85 },
      { capability: 'trade_compliance', confidence: 0.80 }
    ],
    typicalNaics: ['541512', '541511', '561621', '541519'],
    typicalSetAsides: ['SB', '8(a)']
  },
  {
    id: 'cisa',
    name: 'Cybersecurity and Infrastructure Security Agency',
    aliases: ['CISA'],
    department: 'Department of Homeland Security',
    domain: 'homeland_security',
    subDomains: ['cybersecurity', 'critical_infrastructure'],
    proves: [
      { capability: 'cybersecurity', confidence: 0.98 },
      { capability: 'critical_infrastructure_protection', confidence: 0.95 },
      { capability: 'threat_intelligence', confidence: 0.90 },
      { capability: 'incident_response', confidence: 0.90 }
    ],
    typicalNaics: ['541512', '541511', '541519', '541690'],
    typicalSetAsides: ['SB', '8(a)']
  },
  {
    id: 'fema',
    name: 'Federal Emergency Management Agency',
    aliases: ['FEMA'],
    department: 'Department of Homeland Security',
    domain: 'homeland_security',
    subDomains: ['emergency_management', 'disaster_response', 'recovery'],
    proves: [
      { capability: 'emergency_management_systems', confidence: 0.95 },
      { capability: 'disaster_response', confidence: 0.95 },
      { capability: 'grants_management', confidence: 0.85 },
      { capability: 'crisis_communications', confidence: 0.85 }
    ],
    typicalNaics: ['541512', '541611', '541690', '561210'],
    typicalSetAsides: ['SB', '8(a)']
  },

  // ============================================================
  // GENERAL SERVICES ADMINISTRATION
  // ============================================================
  {
    id: 'gsa',
    name: 'General Services Administration',
    aliases: ['GSA'],
    domain: 'general_services',
    subDomains: ['acquisition', 'real_estate', 'technology'],
    proves: [
      { capability: 'federal_acquisition', confidence: 0.95 },
      { capability: 'gsa_schedule_experience', confidence: 0.90 },
      { capability: 'shared_services', confidence: 0.85 },
      { capability: 'federal_buildings', confidence: 0.80 }
    ],
    typicalNaics: ['541512', '541511', '531210', '541611'],
    typicalSetAsides: ['SB', '8(a)']
  },
  {
    id: 'fas',
    name: 'Federal Acquisition Service',
    aliases: ['FAS', 'GSA FAS'],
    department: 'General Services Administration',
    domain: 'general_services',
    subDomains: ['acquisition', 'it_services', 'professional_services'],
    proves: [
      { capability: 'federal_acquisition', confidence: 0.95 },
      { capability: 'it_schedule_work', confidence: 0.90 },
      { capability: 'professional_services_schedule', confidence: 0.90 }
    ],
    typicalNaics: ['541512', '541511', '541611', '541690'],
    typicalSetAsides: ['SB', '8(a)']
  },

  // ============================================================
  // DEPARTMENT OF TREASURY
  // ============================================================
  {
    id: 'treasury',
    name: 'Department of the Treasury',
    aliases: ['Treasury', 'US Treasury', 'Treasury Department'],
    domain: 'finance',
    subDomains: ['financial_management', 'taxation', 'economic_policy'],
    proves: [
      { capability: 'financial_systems', confidence: 0.95 },
      { capability: 'fiscal_management', confidence: 0.90 },
      { capability: 'compliance_systems', confidence: 0.85 },
      { capability: 'secure_financial_data', confidence: 0.90 }
    ],
    typicalNaics: ['541512', '541511', '541611', '541219'],
    typicalSetAsides: ['SB', '8(a)']
  },
  {
    id: 'irs',
    name: 'Internal Revenue Service',
    aliases: ['IRS'],
    department: 'Department of the Treasury',
    domain: 'finance',
    subDomains: ['taxation', 'compliance', 'financial_data'],
    proves: [
      { capability: 'tax_systems', confidence: 0.95 },
      { capability: 'large_scale_data_processing', confidence: 0.95 },
      { capability: 'compliance_systems', confidence: 0.90 },
      { capability: 'secure_financial_data', confidence: 0.95 }
    ],
    typicalNaics: ['541512', '541511', '541219', '541611'],
    typicalSetAsides: ['SB', '8(a)']
  },

  // ============================================================
  // DEPARTMENT OF JUSTICE
  // ============================================================
  {
    id: 'doj',
    name: 'Department of Justice',
    aliases: ['DOJ', 'Justice Department'],
    domain: 'justice',
    subDomains: ['law_enforcement', 'legal', 'corrections'],
    proves: [
      { capability: 'law_enforcement_systems', confidence: 0.95 },
      { capability: 'criminal_justice', confidence: 0.90 },
      { capability: 'case_management', confidence: 0.85 },
      { capability: 'secure_data_handling', confidence: 0.90 }
    ],
    typicalNaics: ['541512', '541511', '541611', '541990'],
    typicalSetAsides: ['SB', '8(a)']
  },
  {
    id: 'fbi',
    name: 'Federal Bureau of Investigation',
    aliases: ['FBI'],
    department: 'Department of Justice',
    domain: 'justice',
    subDomains: ['law_enforcement', 'counterterrorism', 'cybercrime'],
    proves: [
      { capability: 'law_enforcement_systems', confidence: 0.95 },
      { capability: 'intelligence_systems', confidence: 0.90 },
      { capability: 'cybersecurity', confidence: 0.85 },
      { capability: 'forensic_systems', confidence: 0.85 },
      { capability: 'high_security_work', confidence: 0.95 }
    ],
    typicalNaics: ['541512', '541511', '541519', '541690'],
    typicalSetAsides: ['SB', '8(a)']
  },

  // ============================================================
  // DEPARTMENT OF ENERGY
  // ============================================================
  {
    id: 'doe',
    name: 'Department of Energy',
    aliases: ['DOE', 'Energy Department'],
    domain: 'energy',
    subDomains: ['nuclear', 'renewable_energy', 'national_labs'],
    proves: [
      { capability: 'energy_sector_work', confidence: 0.95 },
      { capability: 'scientific_computing', confidence: 0.90 },
      { capability: 'nuclear_related_work', confidence: 0.80 },
      { capability: 'high_performance_computing', confidence: 0.85 }
    ],
    typicalNaics: ['541512', '541511', '541330', '541715'],
    typicalSetAsides: ['SB', '8(a)']
  },

  // ============================================================
  // DEPARTMENT OF TRANSPORTATION
  // ============================================================
  {
    id: 'dot',
    name: 'Department of Transportation',
    aliases: ['DOT', 'Transportation Department'],
    domain: 'transportation',
    subDomains: ['highways', 'aviation', 'rail', 'maritime'],
    proves: [
      { capability: 'transportation_systems', confidence: 0.95 },
      { capability: 'infrastructure_management', confidence: 0.90 },
      { capability: 'safety_systems', confidence: 0.85 },
      { capability: 'logistics_systems', confidence: 0.85 }
    ],
    typicalNaics: ['541512', '541511', '541330', '541611'],
    typicalSetAsides: ['SB', '8(a)', 'DBE']
  },
  {
    id: 'faa',
    name: 'Federal Aviation Administration',
    aliases: ['FAA'],
    department: 'Department of Transportation',
    domain: 'transportation',
    subDomains: ['aviation', 'air_traffic', 'safety'],
    proves: [
      { capability: 'aviation_systems', confidence: 0.95 },
      { capability: 'air_traffic_management', confidence: 0.90 },
      { capability: 'safety_critical_systems', confidence: 0.95 },
      { capability: 'real_time_systems', confidence: 0.90 }
    ],
    typicalNaics: ['541512', '541511', '541330', '541690'],
    typicalSetAsides: ['SB', '8(a)']
  },

  // ============================================================
  // DEPARTMENT OF STATE
  // ============================================================
  {
    id: 'state',
    name: 'Department of State',
    aliases: ['State Department', 'DOS', 'State'],
    domain: 'state',
    subDomains: ['diplomacy', 'foreign_affairs', 'consular'],
    proves: [
      { capability: 'diplomatic_systems', confidence: 0.95 },
      { capability: 'international_operations', confidence: 0.90 },
      { capability: 'secure_communications', confidence: 0.90 },
      { capability: 'foreign_language_support', confidence: 0.80 }
    ],
    typicalNaics: ['541512', '541511', '541611', '541930'],
    typicalSetAsides: ['SB', '8(a)']
  },

  // ============================================================
  // ENVIRONMENTAL PROTECTION AGENCY
  // ============================================================
  {
    id: 'epa',
    name: 'Environmental Protection Agency',
    aliases: ['EPA'],
    domain: 'environment',
    subDomains: ['environmental_protection', 'compliance', 'research'],
    proves: [
      { capability: 'environmental_systems', confidence: 0.95 },
      { capability: 'environmental_compliance', confidence: 0.90 },
      { capability: 'scientific_data_management', confidence: 0.85 },
      { capability: 'regulatory_systems', confidence: 0.85 }
    ],
    typicalNaics: ['541512', '541511', '541620', '541380'],
    typicalSetAsides: ['SB', '8(a)']
  },

  // ============================================================
  // INTELLIGENCE COMMUNITY
  // ============================================================
  {
    id: 'cia',
    name: 'Central Intelligence Agency',
    aliases: ['CIA'],
    domain: 'intelligence',
    subDomains: ['intelligence', 'covert_operations', 'analysis'],
    proves: [
      { capability: 'intelligence_systems', confidence: 0.98 },
      { capability: 'high_security_work', confidence: 0.98 },
      { capability: 'classified_work', confidence: 0.98 },
      { capability: 'intelligence_analysis', confidence: 0.95 }
    ],
    typicalNaics: ['541512', '541511', '541519', '541690'],
    typicalSetAsides: ['SB', '8(a)']
  },
  {
    id: 'nsa',
    name: 'National Security Agency',
    aliases: ['NSA'],
    domain: 'intelligence',
    subDomains: ['signals_intelligence', 'cybersecurity', 'cryptography'],
    proves: [
      { capability: 'intelligence_systems', confidence: 0.98 },
      { capability: 'cybersecurity', confidence: 0.98 },
      { capability: 'cryptography', confidence: 0.95 },
      { capability: 'high_security_work', confidence: 0.98 },
      { capability: 'classified_work', confidence: 0.98 }
    ],
    typicalNaics: ['541512', '541511', '541519', '541690'],
    typicalSetAsides: ['SB', '8(a)']
  },

  // ============================================================
  // NASA
  // ============================================================
  {
    id: 'nasa',
    name: 'National Aeronautics and Space Administration',
    aliases: ['NASA'],
    domain: 'science',
    subDomains: ['space', 'aeronautics', 'research'],
    proves: [
      { capability: 'aerospace_systems', confidence: 0.95 },
      { capability: 'scientific_computing', confidence: 0.95 },
      { capability: 'mission_critical_systems', confidence: 0.95 },
      { capability: 'research_it', confidence: 0.90 }
    ],
    typicalNaics: ['541512', '541511', '541330', '541715'],
    typicalSetAsides: ['SB', '8(a)']
  }
];

/**
 * Find an agency by ID, name, or alias
 */
export function findAgency(term: string): AgencyDomainNode | undefined {
  const normalizedTerm = term.toLowerCase().trim();

  return AGENCY_DOMAINS.find(agency =>
    agency.id === normalizedTerm ||
    agency.name.toLowerCase() === normalizedTerm ||
    agency.aliases.some(alias => alias.toLowerCase() === normalizedTerm)
  );
}

/**
 * Search agencies (partial match)
 */
export function searchAgencies(term: string): AgencyDomainNode[] {
  const normalizedTerm = term.toLowerCase().trim();

  return AGENCY_DOMAINS.filter(agency =>
    agency.id.includes(normalizedTerm) ||
    agency.name.toLowerCase().includes(normalizedTerm) ||
    agency.aliases.some(alias => alias.toLowerCase().includes(normalizedTerm))
  );
}

/**
 * Get agencies by domain
 */
export function getAgenciesByDomain(domain: string): AgencyDomainNode[] {
  return AGENCY_DOMAINS.filter(agency => agency.domain === domain);
}

/**
 * Get sub-agencies of a parent department
 */
export function getSubAgencies(departmentName: string): AgencyDomainNode[] {
  return AGENCY_DOMAINS.filter(agency =>
    agency.department?.toLowerCase() === departmentName.toLowerCase()
  );
}

/**
 * Get all unique domains
 */
export function getAllDomains(): string[] {
  const domains = new Set<string>();
  for (const agency of AGENCY_DOMAINS) {
    domains.add(agency.domain);
  }
  return Array.from(domains).sort();
}

/**
 * Get capability IDs that agencies can prove
 */
export function getAllAgencyCapabilities(): string[] {
  const capabilities = new Set<string>();

  for (const agency of AGENCY_DOMAINS) {
    for (const proof of agency.proves) {
      capabilities.add(proof.capability);
    }
  }

  return Array.from(capabilities).sort();
}
